import { t } from "i18next";
import cx from "classnames";

type AnalyticsDataTableSkeletonProps = {
  /** Data columns to draw. The real column set is only known once the API responds, so this is an approximation of the expected shape. */
  columnCount: number;
  rowCount: number;
  showSelectionColumn: boolean;
  showActionColumn: boolean;
};

// Cycled so the placeholder rows look like text of differing lengths rather than a uniform block.
const cellBarWidths = ["w-3/4", "w-1/2", "w-5/6", "w-2/3", "w-4/5"];

export const AnalyticsDataTableSkeleton = ({
  columnCount,
  rowCount,
  showSelectionColumn,
  showActionColumn,
}: AnalyticsDataTableSkeletonProps) => {
  const columns = Array.from({ length: Math.max(columnCount, 1) }, (_, i) => i);
  const rows = Array.from({ length: Math.max(rowCount, 1) }, (_, i) => i);

  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">{t("Common.Loading")}...</span>
      <div className="animate-pulse" aria-hidden>
        <div className="flex items-center my-0.5">
          <div className="h-[38px] w-full rounded-lg bg-gray-100 lg:w-80" />
        </div>
        <div className="relative border rounded-lg mt-2 mb-4 overflow-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {showSelectionColumn && (
                  <th className="w-10 px-3">
                    <div className="mx-auto h-4 w-4 rounded bg-gray-200" />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column}
                    className="h-9 px-6 border-r last:border-r-0"
                  >
                    <div className="h-2.5 w-2/3 rounded bg-gray-200" />
                  </th>
                ))}
                {showActionColumn && (
                  <th className="h-9 w-28 px-6">
                    <div className="ml-auto h-2.5 w-12 rounded bg-gray-200" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={row}>
                  {showSelectionColumn && (
                    <td className="w-10 px-3 py-2.5">
                      <div className="mx-auto h-4 w-4 rounded bg-gray-100" />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column}
                      className="px-6 py-2.5 border-r last:border-r-0"
                    >
                      <div
                        className={cx(
                          "h-4 rounded bg-gray-100",
                          cellBarWidths[(row + column) % cellBarWidths.length],
                        )}
                      />
                    </td>
                  ))}
                  {showActionColumn && (
                    <td className="w-28 px-6 py-2.5">
                      <div className="ml-auto h-6 w-16 rounded bg-gray-100" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDataTableSkeleton;
